import { useId, useState } from 'react'
import AdminTabNav from '../../design-system/patterns/AdminTabNav.jsx'
import { dispatchOpenPantryReceipt } from '../../utils/pantryCleanup.js'

const SORT_OPTIONS = [
  { id: 'recipe', label: 'Recipe' },
  { id: 'category', label: 'Category' },
  { id: 'list', label: 'A–Z' },
  { id: 'aisle', label: 'Aisle' },
]

const TEXT_LIST_LABEL = 'Copy text list'

/** Copy-only share surface — coming-soon delivery stubs stay out of the UI. */
function ShopV2DeliveryCard({ canCopyTextList = false, textListCopied = false, onCopyTextList }) {
  if (!canCopyTextList) return null
  return (
    <div className="shop-v2-delivery-card">
      <p className="eyebrow">Share list</p>
      <div className="shop-v2-delivery-pills">
        <button
          type="button"
          className="shop-v2-delivery-pill shop-v2-delivery-pill--action"
          title="Copy shopping list as plain text"
          onClick={onCopyTextList || undefined}
          aria-label={
            textListCopied
              ? 'Shopping list copied to clipboard'
              : 'Copy shopping list as plain text'
          }
        >
          {textListCopied ? '✓ Copied' : TEXT_LIST_LABEL}
        </button>
      </div>
    </div>
  )
}

export function ShopV2StatChips({ total, checked, aisleCount, mealCount, showChecked = true }) {
  if (total <= 0) return null
  const checkedAccent = checked > 0
  return (
    <div className="shop-v2-stat-chips" role="group" aria-label="Shopping list summary">
      <div className="shop-v2-stat-chip">
        <span className="shop-v2-stat-chip-label">Items</span>
        <span className="shop-v2-stat-chip-value">{total}</span>
      </div>
      {aisleCount > 0 ? (
        <div className="shop-v2-stat-chip">
          <span className="shop-v2-stat-chip-label">Aisles</span>
          <span className="shop-v2-stat-chip-value">{aisleCount}</span>
        </div>
      ) : null}
      {mealCount > 0 ? (
        <div className="shop-v2-stat-chip">
          <span className="shop-v2-stat-chip-label">Meals</span>
          <span className="shop-v2-stat-chip-value">{mealCount}</span>
        </div>
      ) : null}
      {showChecked ? (
        <div className={`shop-v2-stat-chip${checkedAccent ? ' shop-v2-stat-chip--accent' : ''}`}>
          <span className="shop-v2-stat-chip-label">Checked</span>
          <span className="shop-v2-stat-chip-value">{checked}/{total}</span>
        </div>
      ) : null}
    </div>
  )
}

/** Single progress surface for desktop sidebar + mobile bottom (replaces header track). */
export function ShopV2ProgressCard({ checked, total, allDone, showPantryCta = true }) {
  if (total <= 0) return null
  return (
    <div className={`shop-v2-progress-card${allDone ? ' shop-v2-progress-card--done' : ''}`}>
      <div className="shop-v2-progress-card-head">
        <span className="shop-v2-progress-card-title">{allDone ? '✓ All done!' : 'Progress'}</span>
        <span className="shop-v2-progress-card-count">{checked}/{total}</span>
      </div>
      <div
        className="shop-v2-progress-card-bar"
        role="progressbar"
        aria-valuenow={checked}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label="Shopping progress"
      >
        <div
          className="shop-v2-progress-card-fill"
          style={{ width: `${(checked / total) * 100}%` }}
        />
      </div>
      {allDone && showPantryCta ? (
        <button type="button" className="primary shop-v2-update-pantry-btn" onClick={dispatchOpenPantryReceipt}>
          Update pantry with this shop →
        </button>
      ) : null}
    </div>
  )
}

export function ShopV2SortPills({ sortBy, onSortChange }) {
  return (
    <AdminTabNav
      className="shop-v2-sort-pills"
      tabs={SORT_OPTIONS}
      activeTab={sortBy}
      onTabChange={onSortChange}
      ariaLabel="Sort shopping list"
      variant="link"
    />
  )
}

export function ShopV2Sidebar({
  checked,
  total,
  allDone,
  selectedStore,
  onOpenStoreLayout,
  canCopyTextList = false,
  textListCopied = false,
  onCopyTextList,
}) {
  const storeLabel = selectedStore?.name || 'your store'
  const showStoreCta = Boolean(total > 0 && selectedStore && onOpenStoreLayout)
  return (
    <aside className="shop-v2-sidebar" aria-label="Shop actions">
      <ShopV2ProgressCard checked={checked} total={total} allDone={allDone} />

      <div className="shop-v2-hero-card">
        <p className="eyebrow">Take it to the store</p>
        <h3 className="shop-v2-hero-title">In-store list</h3>
        <p className="shop-v2-hero-body">
          Aisle-by-aisle order mapped to {storeLabel}. Tap items as you go.
        </p>
        {showStoreCta ? (
          <button type="button" className="shop-v2-hero-btn" onClick={onOpenStoreLayout}>
            View store layout →
          </button>
        ) : null}
      </div>

      <ShopV2DeliveryCard
        canCopyTextList={canCopyTextList}
        textListCopied={textListCopied}
        onCopyTextList={onCopyTextList}
      />
    </aside>
  )
}

/** Mobile footer — progress always visible; CTAs collapse behind a toggle. Hidden when empty. */
export function ShopV2MobileBottomBar({
  checked,
  total,
  allDone,
  selectedStore = null,
  onOpenStoreLayout = null,
  canCopyTextList = false,
  textListCopied = false,
  onCopyTextList,
}) {
  const actionsId = useId()
  const [expanded, setExpanded] = useState(false)
  const showStoreCta = Boolean(total > 0 && selectedStore && onOpenStoreLayout)
  const showDelivery = Boolean(canCopyTextList)
  const showPantryCta = Boolean(allDone && total > 0)
  const showProgress = total > 0
  const hasExpandableActions = showStoreCta || showDelivery || showPantryCta
  if (!showProgress && !showStoreCta && !showDelivery) return null

  const expandedClass = hasExpandableActions
    ? expanded
      ? ' shop-v2-mobile-bottom--expanded'
      : ' shop-v2-mobile-bottom--collapsed'
    : ''
  const progressPct = showProgress ? (checked / total) * 100 : 0

  return (
    <div
      className={`shop-v2-mobile-bottom shop-v2-mobile-bottom--fixed${allDone ? ' shop-v2-mobile-bottom--done' : ''}${expandedClass}`}
      role="region"
      aria-label="Shop progress and actions"
    >
      {hasExpandableActions ? (
        <>
          <button
            type="button"
            className="shop-v2-mobile-bottom-toggle"
            aria-expanded={expanded}
            aria-controls={actionsId}
            aria-label={
              showProgress
                ? `${allDone ? 'All done' : 'Progress'} ${checked} of ${total}. ${expanded ? 'Hide' : 'Show'} shop actions`
                : expanded
                  ? 'Hide shop actions'
                  : 'Show shop actions'
            }
            onClick={() => setExpanded((open) => !open)}
          >
            <span className="shop-v2-mobile-bottom-toggle-row" aria-hidden="true">
              <span className="shop-v2-mobile-bottom-toggle-label">
                <span className="shop-v2-progress-card-title">
                  {allDone ? '✓ All done!' : 'Progress'}
                </span>
                {showProgress ? (
                  <span className="shop-v2-progress-card-count">{checked}/{total}</span>
                ) : null}
              </span>
              <span className="shop-v2-mobile-bottom-chevron">
                <i className={`fas fa-chevron-${expanded ? 'down' : 'up'}`} />
              </span>
            </span>
            {showProgress ? (
              <span className="shop-v2-progress-card-bar" aria-hidden="true">
                <span
                  className="shop-v2-progress-card-fill"
                  style={{ width: `${progressPct}%` }}
                />
              </span>
            ) : null}
          </button>
          <div
            id={actionsId}
            className="shop-v2-mobile-bottom-actions"
            hidden={!expanded}
          >
            {showPantryCta ? (
              <button
                type="button"
                className="primary shop-v2-update-pantry-btn"
                onClick={dispatchOpenPantryReceipt}
              >
                Update pantry with this shop →
              </button>
            ) : null}
            {showStoreCta ? (
              <button type="button" className="primary shop-v2-mobile-store-btn" onClick={onOpenStoreLayout}>
                <i className="fas fa-map icon-sm" aria-hidden="true" />
                View store layout
              </button>
            ) : null}
            <ShopV2DeliveryCard
              canCopyTextList={canCopyTextList}
              textListCopied={textListCopied}
              onCopyTextList={onCopyTextList}
            />
          </div>
        </>
      ) : (
        <ShopV2ProgressCard checked={checked} total={total} allDone={allDone} />
      )}
    </div>
  )
}
